import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, RefreshCw, Send, AlertTriangle, CheckCircle2 } from "lucide-react";
import { adminClient } from "./api";

const KIND_LABEL = {
  booking_confirmation: "Booking confirmation",
  order_confirmation: "Order confirmation",
  inquiry_received: "Inquiry received",
  review_request: "Review request",
  deposit_reminder: "Deposit reminder",
  day_before: "Day-before reminder",
  on_my_way: "On my way",
  delivery_receipt: "Proof of delivery",
  rebook_nudge: "Rebook nudge (6 mo)",
  claim_received: "Claim received",
  claim_reviewing: "Claim reviewing",
  claim_approved: "Claim approved",
  claim_denied: "Claim denied",
  claim_resolved: "Claim resolved",
  test: "Test",
};

function kindLabel(kind) {
  if (KIND_LABEL[kind]) return KIND_LABEL[kind];
  return kind.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export default function CustomerEmailsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/admin/customer-emails?limit=8");
      setData(data);
    } catch { setData(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data: res } = await adminClient.post("/admin/customer-emails/test");
      const r = res.result || {};
      if (r.status === "sent") toast.success("Sample customer email sent to your inbox");
      else toast.error(`Not delivered: ${r.detail || "unknown error"}`, { duration: 12000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test");
    } finally { setTesting(false); }
  };

  const domain = data?.domain;
  const domainBlocked = !!domain && !domain.verified && domain.status !== "unknown";
  const live = !!domain?.verified || data?.last_status === "sent";

  return (
    <Card data-testid="customer-emails-card" className="p-5 border-2 border-zinc-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#0C2E2B]" />
          <h3 className="font-display font-black text-[#0C2E2B]">Customer Emails</h3>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <Badge data-testid="customer-emails-status-badge" className={`text-[10px] ${live && !domainBlocked ? "bg-emerald-600 hover:bg-emerald-700" : domainBlocked ? "bg-red-600 hover:bg-red-700" : "bg-zinc-400 hover:bg-zinc-500"}`}>
              {domainBlocked ? "DOMAIN NOT VERIFIED" : live ? "LIVE" : data.last_status ? data.last_status.toUpperCase() : "READY"}
            </Badge>
          )}
          <button data-testid="customer-emails-refresh-btn" onClick={load} className="text-zinc-400 hover:text-[#0C2E2B]">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {!data ? (
        <div className="text-xs text-zinc-500 mt-3">{loading ? "Loading…" : "Could not load"}</div>
      ) : (
        <>
          <div className="mt-3 text-[11px] text-zinc-500 flex justify-between gap-2">
            <span>from</span>
            <span data-testid="customer-emails-sender" className="font-mono-data font-bold text-[#0C2E2B] truncate">{data.sender}</span>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1">
            Confirmations on booking / order / inquiry, status updates when you Mark Paid, Ship, Complete, Refund or Cancel, and a "How did we do?" review request 24h after a job is completed.
            {data.bcc_owner && " You're BCC'd on every one; customer replies go to your Gmail."}
          </p>

          {domainBlocked && (
            <div data-testid="customer-emails-domain-hint" className="mt-3 flex items-start gap-1.5 text-[10px] text-red-900 bg-red-50 border-l-2 border-red-500 px-2 py-1.5 rounded-r">
              <AlertTriangle className="w-3 h-3 mt-px shrink-0" />
              <span>Resend says <b>{domain.domain}</b> is {domain.status === "not_added" ? "not added to your account" : domain.status.replace(/_/g, " ")}, so customer emails are bouncing. Add it and its DNS records at resend.com/domains, wait for the green check, then hit "Send sample" again.</span>
            </div>
          )}

          <Button
            data-testid="customer-emails-test-btn"
            variant="outline"
            size="sm"
            onClick={sendTest}
            disabled={testing}
            className="mt-3 w-full rounded-md text-[11px] h-8"
          >
            <Send className="w-3 h-3 mr-1" /> {testing ? "Sending…" : "Send sample confirmation to me"}
          </Button>

          {data.items?.length > 0 && (
            <ul data-testid="customer-emails-log" className="mt-3 divide-y divide-zinc-100 border-t border-zinc-100">
              {data.items.map((e) => (
                <li key={e.id} data-testid={`customer-email-row-${e.id}`} className="py-1.5 flex items-center gap-2 text-[11px]">
                  {e.status === "sent"
                    ? <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                    : <AlertTriangle className="w-3 h-3 text-red-600 shrink-0" />}
                  <span className="font-bold text-[#0C2E2B] truncate">{kindLabel(e.kind)}</span>
                  <span className="text-zinc-400 truncate">→ {e.to}</span>
                  <span className="ml-auto text-zinc-400 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
